import { gql } from '@apollo/client';
import React, { FunctionComponent, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Track } from '@justgiving/react-tracking';

import {
  LeaderboardMemberListQuery,
  PageStatus,
} from '../../../types/__generated__';
import PATHS from '../../constants/paths';
import { useFullPageQuery } from '../../hooks';
import { makePhotoUrlFromAbsoluteUrl } from '../../utils/makePhotoUrl';
import InviteMembersModal from '../InviteMembersModal/InviteMembersModal';
import IsPageOwner from '../IsPageOwner/IsPageOwner';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import MemberDetails from '../MemberDetails/MemberDetails';
import PageSection from '../PageSection/PageSection';

import './MemberList.scss';

const DOCUMENT = gql`
  query LeaderboardMemberList(
    $after: String
    $type: PageType!
    $slug: Slug
    $id: ID
  ) {
    page(type: $type, slug: $slug, id: $id) {
      id
      legacyId
      allowFundraising
      membershipPolicy
      fundraisers: leaderboard(type: FUNDRAISERS, first: 5, after: $after) {
        edges {
          node {
            title
            status
            legacyId
            id
            url
            slug
            owner {
              id
              avatar
              name
            }
            donations(first: 1) {
              totalCount
            }
            donationSummary {
              totalAmount {
                value
                currencyCode
              }
            }
            targetWithCurrency {
              value
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalCount
      }
    }
  }
`;

export type MemberListProps = {
  listRef: React.RefObject<HTMLElement>;
};

const LeaderBoardMembersList: FunctionComponent<MemberListProps> = props => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const { listRef } = props;

  const { loading, error, data, fetchMore } = useFullPageQuery<
    LeaderboardMemberListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (error || !data?.page?.fundraisers) {
    return null;
  }

  const {
    page: { fundraisers, membershipPolicy, legacyId },
  } = data;
  const members = fundraisers;
  const membersCount = (members && members.totalCount) || 0;
  const pageInfo = members && members.pageInfo;

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: fundraisers!.pageInfo.endCursor },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.fundraisers ||
          !fetchMoreResult?.page?.fundraisers
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            fundraisers: {
              ...fetchMoreResult.page.fundraisers,
              edges: [
                ...previousResult.page.fundraisers.edges,
                ...fetchMoreResult.page.fundraisers.edges,
              ],
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={t('members.title')}
      hasBorder
      count={membersCount}
      sectionRef={listRef}
    >
      <ul className="jg-pages-teams-members__list">
        {members &&
          members.edges.map((member, index: number) => (
            <MemberDetails
              key={member.node.legacyId}
              pageUrl={member.node.url}
              fundraisingPageIndex={index}
              fundraisingPagesCount={membersCount}
              name={member.node.owner?.name ?? ''}
              picture={makePhotoUrlFromAbsoluteUrl(member.node.owner?.avatar)}
              fundraisingPageShortName={member.node.slug}
              fundraisingPageName={member.node.title}
              fundraisingPageStatus={member.node.status ?? PageStatus.Inactive}
              fundraisingPageTargetAmount={
                member.node.targetWithCurrency?.value
              }
              fundraisingPageRaisedAmount={
                member.node.donationSummary?.totalAmount?.value ?? 0
              }
              fundraisingPageCurrencyCode={
                member.node.donationSummary?.totalAmount?.currencyCode ?? 'GBP'
              }
            />
          ))}
      </ul>
      {pageInfo.hasNextPage && (
        <LoadMoreButton
          className="jg-pages-teams-members-buttons"
          loading={loading}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'members',
          }}
        >
          <Trans i18nKey="members.showMore">
            Show more
            <span className="jg-sr-only"> members</span>
          </Trans>
        </LoadMoreButton>
      )}
      {membershipPolicy === 'open' && (
        <Track data-qa="join-team-members">
          {track => (
            <a
              data-qa="join-button"
              href={`${PATHS.JOIN_URL}${legacyId}`}
              className="jg-btn jg-btn--themed jg-pages-teams-members-buttons"
              onClick={() =>
                track({
                  action_name: 'link click',
                  subtype: 'button',
                  event_value: 'join the team',
                  sub_section: 'team members',
                })
              }
            >
              <Trans i18nKey="members.join">Join the team</Trans>
            </a>
          )}
        </Track>
      )}
      <IsPageOwner>
        <button
          data-qa="invite-members-button"
          className="jg-btn jg-btn--themed jg-pages-teams-members-buttons"
          onClick={() => {
            setInviteModalOpen(true);
          }}
          type="button"
        >
          <Trans i18nKey="members.invite">Invite team members</Trans>
        </button>
        <InviteMembersModal
          isModalOpen={inviteModalOpen}
          onCloseModal={() => {
            setInviteModalOpen(false);
          }}
        />
      </IsPageOwner>
    </PageSection>
  );
};

export default LeaderBoardMembersList;
